import { html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { BeaconPopover } from '@trp-beacon-ui-kit/foundation/types/components';

import { PopBaseComponent } from '../../../../base/pop-base-component';
import { Constants } from './product-table-header.constants';
import { Constants as ProductCenterTableWrapperConstants } from '../../../product-center-table-wrapper/product-center-table-wrapper.constants';
import { IconSVG } from '../../../../common/utils/icon';

import style from './product-table-header.scss?inline';
import type { SortByOption } from './product-table-header.model';
import { TableTypeEnum } from '../../../../product-center/store/product-center.store.model';

/**
 * @tag pop-pc-table-header-component
 * @summary Represents a common component to show Product Table Header for Data tables
 */
@customElement(Constants.TAG_NAME)
export class PopProductTableHeaderComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }
  static readonly tagName = Constants.TAG_NAME;

  @property({ attribute: Constants.DATA_SORT_BY_ATTRIBUTE, type: String })
  sortByDropdownValue: string | undefined =
    ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value;

  @property({
    attribute: Constants.DATA_TABLE_TYPE_ATTRIBUTE,
    type: TableTypeEnum,
  })
  tableType: TableTypeEnum = TableTypeEnum.PERFORMANCE;

  private sortByDropdownOptions: SortByOption[] = [];

  constructor() {
    super();
    this.enableI18n = true;
    this.requestUpdate();
  }

  sortByDropdownChangeHandler(event: Event, option?: SortByOption) {
    this.sortByDropdownValue = option?.value;
    const sortByPopover = this.shadowRoot?.querySelector(
      `beacon-popover#product-header-popover-${this.tableType}`,
    ) as BeaconPopover;
    if (sortByPopover && typeof sortByPopover.hide === 'function') {
      sortByPopover.hide();
    }
    this.dispatchEvent(new CustomEvent('sort-by-change', { detail: option }));
  }

  private getOptionFromSortByValue() {
    return (
      this.sortByDropdownOptions.find(
        (option) => option.value === this.sortByDropdownValue,
      )?.option ?? ''
    );
  }

  private renderPopoverDropdown() {
    this.sortByDropdownOptions = [
      {
        option: this.localize(
          ProductCenterTableWrapperConstants.TRANSLATION_KEYS.APIR,
        ),
        value: ProductCenterTableWrapperConstants.FUND_FIELD.APIR,
      },
      {
        option: this.localize(
          ProductCenterTableWrapperConstants.TRANSLATION_KEYS.CURRENCY,
        ),
        value: ProductCenterTableWrapperConstants.FUND_FIELD.BASECURRENCYNAME,
      },
      {
        option: this.localize(
          ProductCenterTableWrapperConstants.TRANSLATION_KEYS.PRODUCT_NAME,
        ),
        value: ProductCenterTableWrapperConstants.FUND_FIELD.FUNDNAME,
      },
      {
        option: this.localize(
          ProductCenterTableWrapperConstants.TRANSLATION_KEYS.PRODUCT_TYPE,
        ),
        value: ProductCenterTableWrapperConstants.FUND_FIELD.VEHICLETYPENAME,
      },
      {
        option: this.localize(
          ProductCenterTableWrapperConstants.TRANSLATION_KEYS.SHARE_CLASS,
        ),
        value: ProductCenterTableWrapperConstants.FUND_FIELD.SHARECLASS,
      },
    ];
    return html`
      <beacon-button
        id="product-header-popover-trigger-${this.tableType}"
        class="beacon-sort-button"
        icon-align="right"
        variant="tertiary"
        scale="mini"
      >
        ${IconSVG.chevronDown({
          slot: ProductCenterTableWrapperConstants.SUFFIX_SLOT,
          customClass: Constants.CHEVRON_CUSTOM_CLASS,
        })}
        ${this.getOptionFromSortByValue()}
      </beacon-button>
      <beacon-popover
        id="product-header-popover-${this.tableType}"
        class="pop-product-center-popover custom-popover"
        opens-by="#product-header-popover-trigger-${this.tableType}"
        placement="bottom-start"
      >
        <beacon-menu>
          <ul class="share-class-list">
            ${this.sortByDropdownOptions.map((option: SortByOption) => {
              return html`
                <li
                  id="${option.value}"
                  class="b-typography-sans-serif--caption--200 b-font-weight-bold"
                >
                  <beacon-button
                    @beacon-event-click=${(event: Event) =>
                      this.sortByDropdownChangeHandler(event, option)}
                    variant="tertiary"
                    scale="mini"
                  >
                    ${option.option}
                  </beacon-button>
                </li>
              `;
            })}
          </ul>
        </beacon-menu>
      </beacon-popover>
    `;
  }

  render() {
    return html`
      <div class="b-flex b-justify-between pop-performance-header-wrapper">
        <div
          class="pop-performance-header pop-performance-title b-typography-sans-serif--caption--100 b-font-weight-bold"
        >
          ${this.localize(
            ProductCenterTableWrapperConstants.TRANSLATION_KEYS.PRODUCT_NAME,
          )}
        </div>
      </div>
      <div class="pop-sort-by-selector">
        <div class="pop-sort-by-label b-typography-sans-serif--caption--100">
          ${this.localize(
            ProductCenterTableWrapperConstants.TRANSLATION_KEYS.SORT_BY,
          )}
        </div>
        ${this.renderPopoverDropdown()}
      </div>
    `;
  }
}
